/**
 * ----------------------------------------------------------------------
 * Helper functions list:
 * Cookies:
 * 1. setCookie - setting a browser cookie.
 * 2. getCookie - get cookie data by name.
 * 3. eraseCookie - remove cookie by name.
 *
 * Function calls:
 * 4. debounce - Used generally for scroll/input change/resize events to
 * avoid updating state too often.
 *
 * DOM Manipulation:
 * 5. bodyScroll - combining object used for body scroll locking.
 * 6. getFocusableElements - get focusable elements within a container.
 * 7. trapFocus - trap focus within a container.
 * 8. removeTrapFocus - remove the trap focus.
 * 9. pauseAllMedia - Pause all page media.
 * 10. getOffsetTop - Get offset from start of document.
 *
 * Data operations:
 * 11. serializeForm - turns formData into a JSON string. Accepts form el.
 * 12. decode - Decode a URI string.
 * 13. deepClone - get deep copy of an object.
 * 14. handleize - transform string to lowercase, replace spaces and low
 * dashes with a dash.
 * 15. fetchConfig - config object for a fetch request.
 * ----------------------------------------------------------------------
 */

/**
 * Debounce - group a series of sequential calls to a function
 * into a single call to that function after it stops getting
 * called for `wait` amount of ms.
 * @param {Function} fn
 * @param {Number} wait
 */
const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

/**
 * Set cookie.
 * @param {String} name
 * @param {String} value
 * @param {Number} days
 * @returns Void
 */
const setCookie = (name, value, days) => {
  var expires = '';
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
};

/**
 * Get cookie data.
 * @param {String} name
 * @returns {String|Null}
 */
const getCookie = (name) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Erase cookie value
 * @param {String} name
 */
const eraseCookie = (name) => {
  document.cookie = name + '=; Max-Age=-99999999';
};

/**
 * Body Scroll Lock global object.
 * Use this to ensure body scroll is locked across browsers and OS.
 * More info: https://github.com/willmcpo/body-scroll-lock#usage-examples
 */
const bodyScroll = {
  lock(container) {
    this.toggleBodyClass(true);
    bodyScrollLock.disableBodyScroll(container, {
      reserveScrollBarGap: true,
      allowTouchMove: (el) => {
        while (el && el !== document.body) {
          if (el.getAttribute('body-scroll-lock-ignore') !== null) {
            return true;
          }

          el = el.parentElement;
        }
      },
    });
  },
  unlock(container) {
    this.toggleBodyClass();
    bodyScrollLock.enableBodyScroll(container);
  },
  clear() {
    this.toggleBodyClass();
    bodyScrollLock.clearAllBodyScrollLocks();
  },
  toggleBodyClass(flag = false) {
    document.body.classList.toggle('is-locked', flag);
  },
};

/**
 * Get all focusable elements within a given container.
 * @param {DOM Element} container
 * @returns
 */
const getFocusableElements = (container) => {
  const selectors = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable]',
    'area',
    'input:not([type=hidden]):enabled',
    'select:enabled',
    'textarea:enabled',
    'object',
    'iframe',
  ];
  return Array.from(container.querySelectorAll(selectors.join(',')));
};

const trapFocusHandlers = {};

/**
 * Remove trap focus.
 * @param {DOM Element} elementToFocus
 */
const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
};

/**
 * Trap focus within a container.
 * @param {DOM Element} container to trap focus within
 * @param {DOM Element} elementToFocus on when focus trapped
 */
const trapFocus = (container, elementToFocus = container) => {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
};

/**
 * Adjust viewport-height css variable.
 */
let pageScrollPosition = 0;

['load', 'scroll', 'resize'].forEach((eventName) => {
  window.addEventListener(eventName, (e) => {
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`,
    );

    const header = document.querySelector('.header .header__inner');

    if (header) {
      document.documentElement.style.setProperty(
        '--header-height',
        `${header.offsetHeight}px`,
      );
    }

    const promoBar = document.querySelector('[data-promo-bar]');

    if (promoBar) {
      document.documentElement.style.setProperty(
        '--promobar-height',
        `${promoBar.offsetHeight}px`,
      );
    }

    if (!document.body.classList.contains('is-locked')) {
      pageScrollPosition = window.scrollY;

      document.documentElement.style.setProperty(
        '--scroll-position',
        `${pageScrollPosition}px`,
      );
    }

    document.body.classList.toggle('is-scroll', pageScrollPosition > 0);
  });
});

/**
 * Close Details element on Esc key press.
 * @param {Event Object} event
 */
const onKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
};

/**
 * Serialize form.
 * @param {DOM Element} form
 * @returns {String} JSON Object
 */
const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

/**
 * Deep cloning of Object.
 * @param {Object} obj
 * @returns {Object} clone
 */
const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Handleize a string. Lowercase and replace spaces and low dashes
 * with a dash.
 * @param {String} str
 * @returns {String} Handleized string
 */
const handleize = (str) => str.replace(/[ /_]/g, '-').toLowerCase();

/**
 * Decode URI string
 * @param {String} str
 * @returns {String} Decoded string
 */
const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ');

/**
 * Get element offset from start of document.
 * @param {DOM Element} element
 * @returns {Number} Offset top
 */
const getOffsetTop = (element) => {
  let offsetTop = 0;

  do {
    if (!isNaN(element.offsetTop)) {
      offsetTop += element.offsetTop;
    }
  } while ((element = element.offsetParent));

  return offsetTop;
};

/**
 * Format money
 * @param {Number} number Original shopify price value
 * @returns String
 */
const formatMoney = (number) => {
  const { locale, country, currency } = Shopify;

  return new Intl.NumberFormat(`${locale}-${country}`, {
    style: 'currency',
    currency: currency.active,
  }).format(number / 100);
};

/**
 * Pause all media on page.
 */
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*',
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => {
    if (!video.controls && video.muted) {
      return;
    }

    video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/**
 * Configuration object for a fetch request.
 * @param {String} type of content.
 * @returns {Object} The fetch configuration.
 */
const fetchConfig = (type = 'json') => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': `application/${type}`,
      'Accept': `application/${type}`,
    },
  };
};

/**
 * Handle details aria attributes.
 */
document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open'),
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open'),
    );
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

/**
 * Menu Drawer Custom element class.
 */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );

    document
      .querySelector('[data-search-btn]')
      ?.addEventListener('click', () => {
        const megamenu = document.querySelector('megamenu-element');

        this.querySelector('summary').click();

        if (megamenu) {
          megamenu.querySelector('input[type="search"]')?.focus();
        } else {
          this.querySelector('input[type="search"]')?.focus();
        }
      });
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    this.toggleMenuDrawerOpen(event, summaryElement, isOpen);
  }

  toggleMenuDrawerOpen(event, summaryElement, isOpen) {
    if (isOpen) {
      event.preventDefault();
      this.closeMenuDrawer(summaryElement);

      return;
    }

    this.openMenuDrawer(summaryElement);
  }

  openMenuDrawer(summaryElement) {
    const megamenu = document.querySelector('megamenu-element');
    const details = this.getDetailsElement(summaryElement);
    const overflowContainer = this.getOverflowElement(details);

    summaryElement.setAttribute('aria-expanded', true);

    if (megamenu) {
      megamenu.open();

      return;
    }

    setTimeout(() => {
      details.classList.add('menu-opening');
    }, 10);

    bodyScroll.lock(overflowContainer);
  }

  closeMenuDrawer(summaryElement = false) {
    const megamenu = document.querySelector('megamenu-element');
    const detailsElement = this.getDetailsElement(summaryElement);
    const subMenuParent = this.getDetailsElement(detailsElement.parentElement);

    if (megamenu) {
      megamenu.close();

      this.closeAnimation(detailsElement);

      return;
    }

    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelectorAll('details').forEach((details) => {
      setTimeout(() => {
        details.classList.remove('menu-opening');
        this.closeAnimation(details);
      }, 400);
    });

    this.closeAnimation(detailsElement);

    this.querySelector('predictive-search-mobile').close(true);

    if (detailsElement.parentElement.nodeName === 'MENU-DRAWER') {
      bodyScroll.clear();

      return;
    }

    if (subMenuParent) {
      bodyScroll.lock(this.getOverflowElement(subMenuParent));
    }
  }

  getDetailsElement(summaryElement) {
    return summaryElement.closest('details');
  }

  getOverflowElement(detailsElement) {
    return detailsElement.querySelector('[data-overflow]');
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 500) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

/**
 * Product selector
 */
class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.submitButton = this.form.elements['add'];
    this.backOrderButtonWrapper = this.form.querySelector(
      '[data-backorder-wrapper]',
    );
    this.backorderProperty = this.form.querySelector(
      '[data-backorder-property]',
    );
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartItems = document.querySelector('cart-items');
    this.formNotify = this.querySelector('component-form-notify');
    this.dispatchVariant = this.hasAttribute('data-dispatch-variant');
    this.destinationPrice = document.querySelector(this.dataset.priceId);
    this.carouselValues = this.querySelectorAll('[data-carousel-values]');
    this.prices = JSON.parse(
      this.querySelector('[data-variants-prices]').textContent,
    );
    this.collectionCard = this.hasAttribute('data-collection-card');
    this.variants = {};
    this.currentProduct = {};
    this.hardwareColourOptions = this.querySelectorAll(
      '[data-hardware-option]',
    );
    this.sizeOptions = this.querySelectorAll('[data-size-option]');

    if (this.collectionCard) {
      this.swatches = this.querySelectorAll(
        '[data-colour-option] [data-swatch]',
      );
      this.products = JSON.parse(
        this.querySelector('[data-products-json][type="application/json"]')
          .textContent,
      );
      this.currentColour = this.querySelector(
        '[data-colour-option] [data-swatch].is-active',
      ).dataset.value;

      this.updateVariants();

      this.swatches.forEach((swatch) =>
        swatch.addEventListener('click', (event) => {
          this.updateSwatches(event);

          this.updateVariants();

          this.updateProductData();

          this.dispatchEvent(new CustomEvent('change'));
        }),
      );
    } else {
      this.variants = JSON.parse(
        this.querySelector('[data-variants-json][type="application/json"]')
          .textContent,
      );
    }

    this.addEventListener('change', this.onVariantChange.bind(this));

    this.sizeOptions.forEach((sizeOption) => {
      sizeOption.addEventListener('change', () => {
        this.updateSessionSizeValue();

        document
          .querySelectorAll('product-selector, main-product-selector')
          .forEach((productSelector) => {
            productSelector.dispatchEvent(
              new CustomEvent('change-size-option'),
            );
          });
      });
    });

    this.addEventListener(
      'change-size-option',
      this.initSessionSizeValue.bind(this),
    );

    this.hardwareColourOptions.forEach((hardwareColourOption) => {
      hardwareColourOption.addEventListener('change', () => {
        this.onChangeHardwareColourOption(hardwareColourOption);
      });
    });
  }

  async onChangeHardwareColourOption(option) {
    await this.changeCartHardwareColourAttribute(option.value);

    this.changeHardwareColourImages();

    document.dispatchEvent(new Event('hardware-images:load'));
  }

  changeHardwareColourImages() {
    fetch(`${window.location.href}`)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHtml = new DOMParser().parseFromString(
          responseText,
          'text/html',
        );
        changeHardwareImages(responseHtml);

        document.dispatchEvent(new Event('hardware-images:loaded'));
      })
      .catch((error) => {
        console.error(error);
      });
  }

  async changeCartHardwareColourAttribute(attribute) {
    const formData = new FormData();
    formData.append('attributes[hardware_colour_option]', attribute);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    await fetch(`${routes.cart_update_url}`, {
      ...config,
      body: formData,
    });
  }

  updateSwatches(event) {
    this.swatches.forEach((swatch) => {
      swatch.classList.toggle('is-active', swatch === event.target);

      if (swatch === event.target) {
        this.currentColour = swatch.dataset.value;

        this.updateColourLabel();
      }
    });
  }

  updateColourLabel() {
    const colourLabel = this.querySelector('[data-current-colour]');

    if (!colourLabel) {
      return;
    }

    colourLabel.textContent = this.currentColour;
  }

  updateVariants() {
    if (!this.collectionCard) {
      return;
    }

    this.currentProduct = this.products.find(
      (product) => product.colour_name === this.currentColour,
    );

    this.variants = this.currentProduct.variants;
  }

  updateProductData() {
    const imageContainer = this.closest(
      '[data-product-contnainer]',
    )?.querySelector('[data-media-container]');
    const titleContainer = this.closest(
      '[data-product-contnainer]',
    )?.querySelector('[data-title-container]');

    if (!this.currentProduct) {
      return;
    }

    if (imageContainer) {
      imageContainer.href = `/products/${this.currentProduct.handle}`;

      imageContainer.querySelector('img').src =
        this.currentProduct.featured_image;
    }

    if (titleContainer) {
      titleContainer.href = `/products/${this.currentProduct.handle}`;

      titleContainer.textContent = this.currentProduct.title.replace(
        'for Apple Watch',
        '',
      );
    }
  }

  connectedCallback() {
    this.updateOptions();
    this.updateVariant();
    this.toggleFormNotify();
    this.toggleBackorderButton();
    this.initCarouselValues();
    this.initSessionSizeValue();
    this.initHardwareColourOption();
    this.updateSizeGuideModal();
  }

  onSubmitHandler(event) {
    event.preventDefault();

    if (this.dispatchVariant) {
      if (this.classList.contains('is-selected')) {
        const variantSelectedEvent = new CustomEvent(
          'product-selector::variant::unselected',
          {
            detail:
              this.currentVariant ||
              this.variants.find(
                (variant) =>
                  String(variant.id) ===
                  this.querySelector('[name="id"]').value,
              ),
          },
        );
        const listenerElement = this.closest('[data-variant-event-listener]');

        if (!listenerElement) {
          return;
        }

        this.submitButton.innerHTML = this.submitButton.dataset.text.replace(
          '[price]',
          this.currentVariantPrice.price_single,
        );

        this.classList.remove('is-selected');

        return listenerElement.dispatchEvent(variantSelectedEvent);
      } else {
        const variantSelectedEvent = new CustomEvent(
          'product-selector::variant::selected',
          {
            detail:
              this.currentVariant ||
              this.variants.find(
                (variant) =>
                  String(variant.id) ===
                  this.querySelector('[name="id"]').value,
              ),
          },
        );
        const listenerElement = this.closest('[data-variant-event-listener]');

        if (!listenerElement) {
          return;
        }

        this.submitButton.innerHTML = window.variantStrings.selected;
        this.classList.add('is-selected');

        return listenerElement.dispatchEvent(variantSelectedEvent);
      }
    }

    this.submitButton.classList.add('disabled');

    const formData = new FormData(this.form);
    const sectionsToRender = (this.cartDrawer || this.cartItems)
      .getSectionsToRender()
      .map((section) => section.section);
    const uniqSections = Array.from(new Set(sectionsToRender));

    formData.append('sections', uniqSections);
    formData.append('sections_url', window.location.pathname);

    Cart.add(formData)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        const quickview = this.closest('modal-quickview');
        const widgetDrawer = this.closest('widget-drawer');

        if (quickview) {
          quickview.hide();
        }

        if (widgetDrawer) {
          widgetDrawer.hide();
        }

        (this.cartDrawer || this.cartItems).renderContents(response);
        document.querySelector('widget-cross-sell')?.fetchContents();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.submitButton.classList.remove('disabled');
      });
  }

  handleErrorMessage(errorMessage = false) {
    const errorWrapper = this.querySelector('[data-error-wrapper]');

    if (errorWrapper && !errorMessage) {
      errorWrapper.classList.add('hidden');
    }

    if (!errorWrapper || !errorMessage) return;

    window.dispatchEvent(
      new CustomEvent('formError', { detail: errorMessage }),
    );
    errorWrapper.classList.toggle('hidden', !errorMessage);

    if (typeof errorMessage == 'string') {
      errorWrapper.textContent = errorMessage || '';
    }
  }

  initHardwareColourOption() {
    const targetOption = this.querySelector(
      '[data-hardware-option][data-option-checked]',
    );

    if (!targetOption) {
      const hardwareFirstOption = this.querySelector('[data-hardware-option]');
      const hardwareOptionPosition = hardwareFirstOption
        ? hardwareFirstOption.dataset.hardwareOptionPosition
        : '';

      if (hardwareOptionPosition != '' && this.currentVariant) {
        const currentSelectedHardware =
          this.currentVariant[`option${hardwareOptionPosition}`];

        this.querySelector(
          `[data-hardware-option][value="${currentSelectedHardware}"]`,
        )?.setAttribute('checked', true);
      }

      if (!this.currentVariant && hardwareFirstOption) {
        hardwareFirstOption.setAttribute('checked', true);
      }

      this.dispatchEvent(new CustomEvent('change'));

      return;
    }

    const selectedOption = this.querySelector('[data-hardware-option]:checked');

    if (selectedOption) {
      selectedOption.removeAttribute('checked');
    }

    targetOption.setAttribute('checked', true);

    this.dispatchEvent(new CustomEvent('change'));
  }

  initSessionSizeValue() {
    const sessionSizeValue = sessionStorage.getItem('watch-size-option');

    if (!sessionSizeValue) {
      const sizeFirstOption = this.querySelector('[data-size-option]');
      const sizeOptionPosition = sizeFirstOption
        ? sizeFirstOption.dataset.sizeOptionPosition
        : '';

      if (sizeOptionPosition != '' && this.currentVariant) {
        const currentSelectedSize =
          this.currentVariant[`option${sizeOptionPosition}`];

        this.querySelector(
          `[data-size-option][value="${currentSelectedSize}"]`,
        )?.setAttribute('checked', true);
      }

      if (!this.currentVariant && sizeFirstOption) {
        sizeFirstOption.setAttribute('checked', true);
      }

      this.dispatchEvent(new CustomEvent('change'));

      return;
    }

    const selectedSizeOption = this.querySelector('[data-size-option]:checked');

    const targetSizeOption = this.querySelector(
      `[data-size-option][value="${sessionSizeValue}"]`,
    );

    if (!targetSizeOption || targetSizeOption.checked == true) {
      return;
    }

    if (selectedSizeOption) {
      selectedSizeOption.removeAttribute('checked');
    }

    targetSizeOption.setAttribute('checked', true);

    this.dispatchEvent(new CustomEvent('change'));
  }

  updateSessionSizeValue() {
    const selectedSize = this.querySelector('[data-size-option]:checked');
    const sessionSizeValue =
      sessionStorage.getItem('watch-size-option') || null;

    if (!selectedSize || selectedSize.value === sessionSizeValue) {
      return;
    }

    sessionStorage.setItem('watch-size-option', selectedSize.value);
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;

    this.updateOptions();
    this.updateVariant();
    this.toggleFormNotify();
    this.toggleAddButton(false, '');
    // window.enableBackorder && this.toggleBackorderButton();
    this.toggleBackorderButton();
    this.handleErrorMessage();
    this.updateSessionSizeValue();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '');
      this.setUnavailable();

      return;
    }

    this.updatePrice();

    // if(window.enableBackorder){
      // this.containerSection = this.closest('.widget-cross-sell__product') || this.closest('product-sticky-bar') || this.closest('.cart-drawer__upsell') || this.closest('main-product') || this.closest('modal-quickview');
      this.containerSection = this;
  
      if (
        this.currentVariant.available &&
        this.currentVariant.inventory_policy == 'continue' &&
        this.currentVariant.inventory_quantity <= 0
      ) {
        this.toggleBackorderButton();
        this.containerSection?.classList.add('backorder');
        if (this.nodeName === 'MAIN-PRODUCT-SELECTOR') {
          document.body.classList.add('backorder');
        }
      } else {
        this.containerSection?.classList.remove('backorder');
        if (this.nodeName === 'MAIN-PRODUCT-SELECTOR') {
          document.body.classList.remove('backorder');
        }
      }
    // }


    if (
      !this.currentVariant.available ||
      (this.currentVariant.inventory_policy == 'continue' &&
        this.currentVariant.inventory_quantity <= 0)
    ) {
      this.toggleAddButton(true, window.variantStrings.soldOut);
    }

    this.updateVariantInput();
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked'),
      (el) => ({ name: el.dataset.name, value: el.value }),
    );
  }

  updateVariant() {
    this.currentVariant = this.variants.find((variant) => {
      return !variant.options
        .map((option, index) => this.options[index]?.value === option)
        .includes(false);
    });

    this.currentVariantPrice = this.prices.find((priceObj) => {
      return priceObj.id === this.currentVariant?.id;
    });

    if (!this.formNotify) {
      return;
    }

    const variantUpdatedEvent = new CustomEvent(
      'product-selector::variant::updated',
      {
        detail: { id: this.currentVariant?.id },
      },
    );

    this.formNotify.dispatchEvent(variantUpdatedEvent);

    this.dispatchEvent(variantUpdatedEvent);
  }

  updateVariantInput() {
    const input = this.querySelector('[name="id"]');
    input.value = this.currentVariant.id;
  }

  setUnavailable() {
    if (!this.submitButton) return;

    this.submitButton.textContent = window.variantStrings.unavailable;

    if (this.destinationPrice) {
      this.destinationPrice.classList.add('visually-hidden');
    }
  }

  toggleAddButton(disable, text) {
    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) this.submitButton.textContent = text;
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.textContent = window.variantStrings.addToCart;
    }
  }

  toggleBackorderButton() {
    if (!this.currentVariant || !this.backOrderButtonWrapper) {
      return;
    }

    const isVisible =
      this.currentVariant.available &&
      this.currentVariant.inventory_management != null &&
      this.currentVariant.inventory_policy == 'continue' &&
      this.currentVariant.inventory_quantity <= 0;

    this.backOrderButtonWrapper.classList.toggle('hidden', !isVisible);

    if (this.backorderProperty) {
      if (isVisible) {
        this.backorderProperty.removeAttribute('disabled');
      } else {
        this.backorderProperty.setAttribute('disabled', true);
      }
    }
  }

  updatePrice() {
    if (this.destinationPrice && this.currentVariantPrice?.price_html) {
      this.destinationPrice.classList.remove('visually-hidden');
      this.destinationPrice.innerHTML = this.currentVariantPrice.price_html;
    }

    this.submitButton.innerHTML = this.submitButton.dataset.text.replace(
      '[price]',
      this.currentVariantPrice.price_single,
    );

    if (this.backOrderButtonWrapper) {
      const backorderButton = this.backOrderButtonWrapper.querySelector(
        'button[type="submit"]',
      );

      if (backorderButton) {
        backorderButton.innerHTML = backorderButton.dataset.text.replace(
          '[price]',
          this.currentVariantPrice.price_single,
        );
      }
    }
  }

  initCarouselValues() {
    this.carouselsWithAnimation = [];

    this.carouselValues.forEach((carousel) => {
      let initialSlide = 0;

      if (carousel.hasAttribute('data-carousel-scroll-to-first')) {
        initialSlide = carousel.querySelectorAll('.swiper-slide').length - 1;
      }

      const carouselInstance = new Swiper(carousel, {
        initialSlide: initialSlide,
        speed: 2000,
        slidesPerView: 'auto',
        spaceBetween: 12,
        pagination: {
          el: carousel.querySelector('.swiper-pagination'),
          type: 'progressbar',
        },
        grabCursor: true,
        nested: true,
      });

      if (carousel.hasAttribute('data-carousel-scroll-to-first')) {
        this.carouselsWithAnimation.push(carouselInstance);
      }
    });
  }

  initCarouselAnimation() {
    this.carouselsWithAnimation.forEach((carousel) => carousel.slideTo(0));
  }

  toggleFormNotify() {
    if (!this.currentVariant || !this.formNotify) {
      return;
    }

    this.formNotify.classList.toggle('hidden', this.currentVariant.available);
  }

  updateSizeGuideModal() {
    this.modalSizeGuideStrapTrigger = this.querySelector(
      'modal-opener[data-modal="#Modal-Sizeguide-strap"]',
    );

    this.modalSizeGuideStrapTrigger?.addEventListener('click', () => {
      const modal = document.querySelector(
        'modal-dialog[id="Modal-Sizeguide-strap"]',
      );
      const modalCustomContentContainer = modal?.querySelector(
        '[data-modal-custom-content]',
      );
      const customContent = this.querySelector(
        '[data-size-guide-strap-content]',
      ).innerHTML;

      if (!modal || !modalCustomContentContainer || !customContent) {
        return;
      }

      modalCustomContentContainer.innerHTML = customContent;
    });
  }
}

customElements.define('product-selector', ProductSelector);

/**
 * Card product
 */
class CardProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.wrapper = this.querySelector('[data-wrapper]');
    this.productSelector = this.querySelector('product-selector');
    this.variants = this.querySelector('[data-variants]');
    // this.initOkendoReviews();
    this.toggleOptions = this.querySelectorAll('[data-toggle-options]').forEach(
      (toggle) => {
        toggle.addEventListener('click', this.handleToggleOptions.bind(this));
      },
    );

    this.querySelector('[data-wishlist-remove]')?.addEventListener(
      'click',
      (event) => {
        const wishlistSection = this.closest('wishlist-products');

        if (!wishlistSection) {
          return;
        }

        wishlistSection.removeProduct(event.target, this);
      },
    );
  }

  async loadVariants() {
    const productHandle = this.dataset.productHandle;
    const productVariant = this.dataset.variantId ? this.dataset.variantId : '';
    const sectionId = this.dataset.sectionId ? this.dataset.sectionId : '';

    window.loadProductQuickViewModal = true;

    this.classList.add('is-loading');

    await fetch(
      `/products/${productHandle}?section_id=card-product-markup&variant=${productVariant}&section-id=${sectionId}`,
    )
      .then((response) => response.text())
      .then((html) => {
        if (!html) {
          return;
        }

        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const variants = parsedHTML.querySelector('[data-variants]');

        if (!variants) {
          return;
        }

        this.wrapper.append(variants);

        this.productSelector = this.wrapper.querySelector('product-selector');

        this.variants = this.querySelector('[data-variants]');

        this.variants
          .querySelectorAll('[data-toggle-options]')
          .forEach((toggle) => {
            toggle.addEventListener(
              'click',
              this.handleToggleOptions.bind(this),
            );
          });

        document.dispatchEvent(
          new CustomEvent('swym:product-quick-view-update'),
        );

        this.productSelector.addEventListener(
          'product-selector::variant::updated',
          (event) => {
            this.updateWishlistBtn(event);
          },
        );
      })
      .catch((e) => {
        console.error(e);

        window.loadProductQuickViewModal = false;
      });
  }

  updateWishlistBtn(event) {
    const wishlistBtn = event.target
      .closest('card-variants')
      ?.querySelector('[data-swaction="addToWishlist"]');
    const modalQuickview = document.querySelector('modal-quickview');

    if (
      (modalQuickview &&
        modalQuickview.hasAttribute('data-open-by-wishlist')) ||
      !wishlistBtn ||
      wishlistBtn.closest('wishlist-products')
    ) {
      return;
    }

    wishlistBtn.setAttribute('data-variant-id', event.detail.id);

    document.dispatchEvent(new CustomEvent('swym:product-quick-view-update'));
  }

  async handleToggleOptions(event) {
    const shouldClose = event.target.dataset.type === 'close';
    const openInModal = event.target.dataset.toggleOptions === 'modal';
    const ajaxLoad = event.target.dataset.loadType === 'ajax';
    const hasOpenQuickView = document.querySelector('modal-quickview[open]');

    if (window.loadProductQuickViewModal || hasOpenQuickView) {
      return;
    }

    if (!shouldClose && ajaxLoad) {
      await this.loadVariants();
    }

    if (openInModal) {
      const modal = document.querySelector('#Quickview-Modal');
      const variants = this.querySelector('card-variants');

      if (!modal) {
        return console.error(`Quickview modal doesn't exist`);
      }

      modal.querySelector('[role="dialog"]').append(variants);

      setTimeout(() => {
        modal.show(this);

        this.initCarouselAnimation();

        if (ajaxLoad) {
          this.querySelector('[data-variants]')?.remove();

          this.classList.remove('is-loading');

          window.loadProductQuickViewModal = false;
        }
      }, 300);

      return;
    }

    this.classList.toggle('has-options-open', !shouldClose);

    if (shouldClose && ajaxLoad) {
      this.variants.addEventListener('transitionend', () => {
        this.variants?.remove();
      });

      return;
    }

    this.variants.addEventListener('transitionend', () => {
      this.initCarouselAnimation();
    });

    if (!shouldClose && ajaxLoad) {
      this.classList.remove('is-loading');
    }
  }

  initOkendoReviews() {
    if (!window.okeWidgetApi) {
      return;
    }

    // @todo: Init add to wishlist button with okendo
  }

  initCarouselAnimation() {
    if (!this.productSelector) {
      return;
    }

    this.productSelector.initCarouselAnimation();
  }
}

customElements.define('card-product', CardProduct);

class FormNotify extends HTMLElement {
  constructor() {
    super();

    this.updateVariantId = this.updateVariantId.bind(this);
    this.handleOnSubmit = this.handleOnSubmit.bind(this);
  }

  connectedCallback() {
    this.variantId = null;
    this.input = this.querySelector('[name="email"]');
    this.submit = this.querySelector('button');
    this.addEventListener(
      'product-selector::variant::updated',
      this.updateVariantId,
    );
    this.submit.addEventListener('click', this.handleOnSubmit);
  }

  updateVariantId(event) {
    this.variantId = event.detail.id;
  }

  handleOnSubmit(event) {
    event.preventDefault();

    if (!this.input.validity.valid) {
      this.input.reportValidity();
      return;
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: this.input.value,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.variantId}`,
            },
          },
        },
      },
    };

    fetch(this.dataset.action, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-05-15',
      },
      body: JSON.stringify(payload),
    })
      .then(() => {
        this.success();

        setTimeout(() => {
          this.reset();
        }, 5000);
      })
      .catch((error) => console.error(error));
  }

  success() {
    this.input.readOnly = true;
    this.input.value = this.input.dataset.successText;
    this.classList.add('is-submitted');
  }

  reset() {
    this.input.readOnly = false;
    this.input.value = '';
    this.classList.remove('is-submitted');
  }
}

customElements.define('component-form-notify', FormNotify);

/**
 * Quantity Input custom element class.
 */
class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    event.target.name === 'increment'
      ? this.input.stepUp()
      : this.input.stepDown();

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

/**
 * Modal opener custom element class.
 * Used in combination with ModalDialog for inserting a dialog
 * trigger in the document.
 * `data-modal` property connects the modal with the modal-opener.
 * Must contain a button element.
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));

      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/**
 * Modal dialog custom element class.
 * Inserts a modal that is connected to a modal-opener.
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.dialogHolder = this.querySelector('[role="dialog"]');
    this.carouselWrapper = this.querySelector('.swiper');
    this.querySelectorAll('[id^="ModalClose-"]').forEach((close) => {
      close.addEventListener('click', this.hide.bind(this, false));
    });
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
    this.initCarousel();
  }

  show(opener) {
    this.openedBy = opener;

    this.setAttribute('open', '');
    trapFocus(this, this.dialogHolder);
    window.pauseAllMedia();

    bodyScroll.lock(this.querySelector('[role="dialog"]'));
  }

  hide() {
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.dialogHolder);
    window.pauseAllMedia();

    bodyScroll.unlock(this.querySelector('[role="dialog"]'));
  }

  initCarousel() {
    if (!this.carouselWrapper || this.querySelector('product-selector')) {
      return;
    }

    this.carousel = new Swiper(this.carouselWrapper, {
      spaceBetween: 20,
      slidesPerView: 1,
      navigation: {
        prevEl: this.querySelector('.swiper-arrow--prev'),
        nextEl: this.querySelector('.swiper-arrow--next'),
      },
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
        dynamicBullets: true,
      },
    });
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalVideo extends ModalDialog {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.deferredMedia = this.querySelector('deferred-media');
  }

  show(opener) {
    super.show(opener);
    this.deferredMedia.loadContent();
  }
}

customElements.define('modal-video', ModalVideo);

class ModalQuickview extends ModalDialog {
  constructor() {
    super();
  }

  connectedCallback() {
    this.content = this.querySelector('[data-content]');

    function swymCallbackFn(swat) {
      document.addEventListener('swym:product-quick-view-update', () => {
        swat.initializeActionButtons('modal-quickview');
      });
    }

    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    window.SwymCallbacks.push(swymCallbackFn);

    this.attachEvents();
  }

  attachEvents() {
    this.addEventListener('click', (event) => {
      if (
        event.target.hasAttribute('data-type') &&
        event.target.getAttribute('data-type') == 'close'
      ) {
        this.hide();
      }

      if (
        event.target.hasAttribute('data-swaction') &&
        event.target.getAttribute('data-swaction') == 'addToWishlist' &&
        event.target.classList.contains('swym-added')
      ) {
        const wishlistProductsSections =
          document.querySelectorAll('wishlist-products');

        wishlistProductsSections.forEach((wishlistPorducts) => {
          if (!this.openedBy) {
            return;
          }

          wishlistPorducts.removeProduct(event.target, this.openedBy);
        });

        if (wishlistProductsSections.length > 0) {
          this.hide();
        }
      }
    });
  }

  show(opener) {
    super.show(opener);

    if (this.openedBy && this.openedBy.closest('wishlist-products')) {
      this.setAttribute('data-open-by-wishlist', true);
    }

    const productRatingWidgets = this.querySelectorAll('product-rating');

    productRatingWidgets.forEach((productRatingWidget) =>
      productRatingWidget.init(),
    );
  }

  hide() {
    super.hide();

    this.content.addEventListener(
      'transitionend',
      this.handleContentAnimationEnd.bind(this),
      {
        once: true,
      },
    );

    this.removeAttribute('data-open-by-wishlist', true);

    setTimeout(() => {
      document
        .querySelectorAll('wishlist-badge')
        .forEach((wishlistBadge) => wishlistBadge.update());
    }, 1000);
  }

  handleContentAnimationEnd() {
    const cardVariants = this.querySelector('card-variants');

    if (cardVariants.hasAttribute('data-load-by-ajax')) {
      this.querySelector('card-variants').remove();
    } else {
      const cardVariantsWrapper =
        this.openedBy?.querySelector('[data-variants]');

      if (!cardVariantsWrapper) {
        return;
      }

      cardVariantsWrapper.append(this.querySelector('card-variants'));
    }
  }
}

customElements.define('modal-quickview', ModalQuickview);

class WidgetDrawer extends ModalDialog {
  constructor() {
    super();
  }

  show(opener) {
    super.show(opener);
    bodyScroll.lock(this.querySelector('[data-widget-overflow]'));
  }

  hide() {
    super.hide();
    bodyScroll.clear();
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchRecommendations();
  }

  async fetchRecommendations() {
    if (!this.dataset.url) {
      return;
    }

    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cartJson = await cartResponse.json();

    if (!cartJson.items[0]) {
      return;
    }

    fetch(`${this.dataset.url}&product_id=${cartJson.items[0].product_id}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const items = html.querySelector(
          '#Widget-Recommendations [data-widget-list]',
        );

        if (!items) {
          return;
        }

        this.querySelector('[data-widget-list]').innerHTML = items.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('widget-drawer', WidgetDrawer);

/**
 * Deferred media custom element class.
 * Must contain a button and a template element
 */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(
        content.querySelector('.deferred-media__wrapper'),
      );
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

/**
 * Cart class for base cart actions
 * More info: https://shopify.dev/docs/api/ajax/reference/cart
 */
class Cart {
  /**
   * Get global live regions to update on cart change.
   * @returns {Array} Live region sections objects
   */
  static getLiveRegions() {
    return [
      {
        id: '#cart-counter',
        section: 'cart-counter',
        selector: '#shopify-section-cart-counter',
      },
    ];
  }

  /**
   * Add items to cart.
   * @param {Object} body Form data
   * @returns {Promise} Resolves with the cart object
   */
  static add(body) {
    if (!body) {
      return undefined;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    return fetch(`${routes.cart_add_url}`, {
      ...config,
      ...{ body },
    });
  }

  /**
   * Update cart quantity, properties and selling plan.
   * @param {String} body JSON object
   * @returns {Promise} Resolves with the cart object
   * @example Body: '{"line":1,"quantity":3}'
   */
  static update(body) {
    if (!body) {
      return undefined;
    }

    return fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body },
    });
  }

  /**
   * Clear all items from cart.
   * @returns {Promise} Promise
   */
  static clear() {
    return fetch(`${routes.cart_clear_url}`, {
      ...fetchConfig(),
    });
  }

  /**
   * Get cart Object.
   * @returns {Promise} Resolves with the cart object
   */
  static get() {
    return fetch(`${routes.cart_url}`, {
      ...fetchConfig(),
    });
  }
}

/**
 * Handlelize
 * @param {String} string
 * @returns {String}
 */
const handlelize = (string) => {
  string = string
    .normalize('NFD')
    .replace(/[\[\]'()"]+/g, '') // Remove apostrophes, square brackets, and other bits and pieces
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/([^\w]+|\s+)/g, '-') // Replace space and other characters by hyphen
    .replace(/\-\-+/g, '-') // Replaces multiple hyphens by one hyphen
    .replace(/(^-+|-+$)/g, '') // Remove extra hyphens from beginning or end of the string
    .toLowerCase(); // To lowercase

  return string;
};

/**
 * Cart class for base cart actions
 */

class WishlistBadge extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    window.SwymCallbacks.push(this.init.bind(this));
  }

  attachEvents() {
    _swat.evtLayer.addEventListener(
      'sw:addedtowishlist',
      this.update.bind(this, true),
    );

    _swat.evtLayer.addEventListener(
      'sw:removedfromwishlist',
      this.update.bind(this),
    );
  }

  async init() {
    this.isEnable = false;

    await this.checkWishlist();

    this.toggleVisible();

    this.attachEvents();
  }

  async update(flag) {
    if (flag) {
      const hasOpenQuickView = document.querySelector('modal-quickview[open]');

      if (hasOpenQuickView) {
        return;
      }
    }

    await this.checkWishlist();

    this.toggleVisible();
  }

  async checkWishlist() {
    const productFetch = new Promise((resolve) => {
      _swat.fetch((products) => resolve(products));
    });

    await productFetch.then(
      (products) => (this.isEnable = products.length > 0 ? true : false),
    );
  }

  toggleVisible() {
    this.classList.toggle('is-visible', this.isEnable);
  }
}

customElements.define('wishlist-badge', WishlistBadge);

/**
 * Change hardware images.
 * @param {DOM} html
 * @returns Void
 */
const changeHardwareImages = (html) => {
  const hardwareMediaContainers = document.querySelectorAll(
    '[data-hardware-media-container]',
  );

  hardwareMediaContainers.forEach((hardwareMediaContainer) => {
    let newHardwareMediaContainer = html.querySelector(
      `[data-hardware-media-container][data-container-id="${hardwareMediaContainer.dataset.containerId}"]`,
    );

    if (
      hardwareMediaContainer.dataset.containerId.startsWith(
        'product-media-card-variants',
      )
    ) {
      if (hardwareMediaContainer.hasAttribute('data-static-content')) {
        newHardwareMediaContainer = html.querySelector(
          `[data-hardware-media-container][data-container-id="product-media-card-variants-${hardwareMediaContainer.dataset.containerId.replace(
            'product-media-card-variants-',
            '',
          )}"]`,
        );
      } else {
        newHardwareMediaContainer = html.querySelector(
          `[data-hardware-media-container][data-container-id="product-media-${hardwareMediaContainer.dataset.containerId.replace(
            'product-media-card-variants-',
            '',
          )}"]`,
        );
      }
    }

    if (!newHardwareMediaContainer) {
      return;
    }

    hardwareMediaContainer.innerHTML = newHardwareMediaContainer.innerHTML;
  });
};

const productReviewBadge = document.querySelector(
  '.product [data-reviews-badge]',
);

productReviewBadge?.addEventListener('click', (event) => {
  event.preventDefault();

  const reviewsWidget = document.querySelector(
    productReviewBadge.getAttribute('href'),
  );

  if (!reviewsWidget) {
    return;
  }

  reviewsWidget.scrollIntoView({
    block: 'start',
    behavior: 'smooth',
  });
});

/**
 * Product rating class
 */
class ProductRating extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  async init() {
    if (this.hasAttribute('is-initialized')) {
      return;
    }

    const ratings = await this.getProductRatings();
    const averageRating = this.getAverageRating(ratings);
    const totalReviews = this.getTotalReviews(ratings);

    this.addStarsClasses(averageRating);
    this.updateAttributes(averageRating, totalReviews);
    this.updateMessage(totalReviews);

    this.setAttribute('is-initialized', true);

    this.observer?.unobserve(this);
  }

  async getProductRatings() {
    try {
      const response = await fetch(
        `https://api.reviews.io/product/rating-batch?store=${Shopify.shop}&sku=${this.dataset.sku}`,
      );

      return await response.json();
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  getAverageRating(ratings) {
    const totalReviews = this.getTotalReviews(ratings);
    const sumAllAverageRatings = ratings.reduce(
      (sum, current) =>
        sum +
        parseFloat(current['average_rating']) *
        parseInt(current['num_ratings']),
      0,
    );
    const averageRating =
      totalReviews === 0
        ? '0.00'
        : (sumAllAverageRatings / totalReviews).toFixed(2);

    return averageRating;
  }

  getTotalReviews(ratings) {
    return ratings.reduce((sum, current) => sum + current['num_ratings'], 0);
  }

  addStarsClasses(averageRating) {
    const integerRating = Math.floor(averageRating);
    const starClass = 'ruk-icon-percentage-star';
    const starIcons = this.querySelectorAll('[data-star-icon]');
    let remainderRating = averageRating - integerRating;

    starIcons.forEach((starIcon, index) => {
      const currentIndex = index + 1;

      if (integerRating >= currentIndex) {
        starIcon.classList.add(`${starClass}--100`);
      } else if (remainderRating >= 0.75) {
        starIcon.classList.add(`${starClass}--75`);
        remainderRating = 0;
      } else if (remainderRating >= 0.5) {
        starIcon.classList.add(`${starClass}--50`);
        remainderRating = 0;
      } else if (remainderRating >= 0.25) {
        starIcon.classList.add(`${starClass}--25`);
        remainderRating = 0;
      }
    });
  }

  updateAttributes(averageRating, totalReviews) {
    const titleContent = this.getAttribute('title');
    const ariaLabelContent = this.getAttribute('aria-label');

    if (titleContent) {
      this.setAttribute(
        'title',
        titleContent
          .replaceAll('[[average_rating]]', averageRating)
          .replaceAll('[[total_reviews]]', totalReviews),
      );
    }

    if (ariaLabelContent) {
      this.setAttribute(
        'aria-label',
        ariaLabelContent
          .replaceAll('[[average_rating]]', averageRating)
          .replaceAll('[[total_reviews]]', totalReviews),
      );
    }
  }

  updateMessage(totalReviews) {
    const messageContainer = this.querySelector('[data-message]');

    if (!messageContainer) {
      return;
    }

    let message = '';

    if (totalReviews === 0) {
      message = messageContainer.dataset.noReviewsMessages;
    } else if (totalReviews > 1) {
      message = messageContainer.dataset.pluralMessage?.replace(
        '{{ count }}',
        totalReviews,
      );
    } else {
      message = messageContainer.dataset.singularMessage?.replace(
        '{{ count }}',
        totalReviews,
      );
    }

    messageContainer.textContent = message;
  }
}

customElements.define('product-rating', ProductRating);


